import { useState, useEffect, useRef, useCallback } from 'react';
import { useLazyQuery, useQuery } from 'data-fetching-client';
import { List as ImmutableList, Map as ImmutableMap, Set as ImmutableSet } from 'immutable';
import { sourceDataMutate } from 'automation-ui-formula-editor/data-sources/utils/mutateDataSourceUtils';
import { useDataSourcesContext } from 'automation-ui-formula-editor/data-sources/DataSourcesContext';
import { GET_DATA_SOURCE_BY_PREFIX } from 'automation-ui-formula-editor/data-sources/queries/getPrefixQueries';
import { rewriteLegacyPrefix } from 'automation-ui-formula-editor/data-sources/utils/isLegacyTokenPrefix';
import { ACTION_OUTPUTS } from 'automation-ui-formula-editor/data-sources/constants/dataSourcesConstants';
import useResolveObjectTypeByIdPromise from 'automation-ui-formula-editor/data-sources/hooks/useResolveObjectTypeByIdPromise';
import useResolvePropertyOptionLabelPromise from 'automation-ui-formula-editor/data-sources/hooks/useResolvePropertyOptionLabelPromise';
export const useGetDataSource = prefixReq => {
  const [data, setData] = useState();
  const [loading, setLoading] = useState(true);

  // pull in our data dictionary to get the mutators we need
  const {
    flowId,
    flowObjectTypeId,
    getActionHeaderTitle
  } = useDataSourcesContext();
  const {
    prefix: prefixUnvalidated,
    options
  } = prefixReq;
  const {
    query = {}
  } = options || {};
  const {
    currentActionId
  } = query;
  const prefix = rewriteLegacyPrefix(prefixUnvalidated);
  const resolveObjectTypeById = useResolveObjectTypeByIdPromise();
  const resolvePropertyOptionLabel = useResolvePropertyOptionLabelPromise();
  const {
    data: queryData,
    loading: queryLoading,
    error,
    refetch
  } = useQuery(GET_DATA_SOURCE_BY_PREFIX, {
    skip: !prefix || prefix === ACTION_OUTPUTS && isNaN(currentActionId),
    variables: {
      flowId,
      flowObjectTypeId,
      prefix,
      query
    }
  });
  useEffect(() => {
    if (queryLoading) {
      return;
    }
    if (error) {
      setLoading(false);
      return;
    }
    if (queryData !== null && queryData !== void 0 && queryData.dataSourceByPrefix) {
      const dataSources = queryData.dataSourceByPrefix.dataSources;
      if (!dataSources || !dataSources[prefix]) {
        setLoading(false);
        return;
      }

      // account for any API response mutation that needs to happen
      sourceDataMutate(dataSources[prefix], {
        getActionHeaderTitle,
        resolveObjectTypeById,
        resolvePropertyOptionLabel
      }).then(mutatedData => {
        setData(mutatedData);
        setLoading(false);
      }).catch(() => {
        setLoading(false);
        setData(queryData.dataSourceByPrefix.dataSources[prefix]);
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryData, error, queryLoading]);
  return {
    data,
    loading,
    error,
    refetch
  };
};
export const useLazyGetDataSource = () => {
  const resolvers = useRef(ImmutableMap());
  const [requestQueue, setRequestQueue] = useState(ImmutableSet());
  const [data, setData] = useState();
  const [loading, setLoading] = useState(true);
  const [queueIsLocked, setQueueIsLocked] = useState(false);

  // pull in our data dictionary to get the mutators we need
  const {
    flowId,
    flowObjectTypeId,
    getActionHeaderTitle
  } = useDataSourcesContext();
  const resolveObjectTypeById = useResolveObjectTypeByIdPromise();
  const resolvePropertyOptionLabel = useResolvePropertyOptionLabelPromise();
  const [getDataSourceQuery, {
    called,
    data: queryData,
    loading: queryLoading,
    error,
    variables
  }] = useLazyQuery(GET_DATA_SOURCE_BY_PREFIX, {
    variables: {
      flowId: null,
      flowObjectTypeId: '',
      prefix: ''
    }
  });
  useEffect(() => {
    if (queueIsLocked) {
      return;
    }
    if (requestQueue && requestQueue.size > 0) {
      setQueueIsLocked(true);
      const currentRequest = requestQueue.first();
      getDataSourceQuery({
        variables: currentRequest
      });
      setRequestQueue(prevRequestQueue => prevRequestQueue.remove(currentRequest));
    }
  }, [requestQueue, queueIsLocked, getDataSourceQuery]);
  useEffect(() => {
    if (!called || queryLoading || !variables) {
      return;
    }
    const dataKey = JSON.stringify(variables);
    const resolversForRequest = resolvers.current.get(dataKey);
    if (error) {
      setLoading(false);
      if (resolversForRequest) {
        resolversForRequest.forEach(resolver => {
          if (resolver.onReject) {
            resolver.onReject(error);
          }
        });
      }
      resolvers.current.remove(dataKey);
      setQueueIsLocked(false);
      return;
    }
    if (queryData) {
      const dataSources = queryData.dataSourceByPrefix.dataSources;
      if (!dataSources || !dataSources[variables.prefix]) {
        setLoading(false);
        if (resolversForRequest) {
          resolversForRequest.forEach(resolver => {
            if (resolver.onReject) {
              resolver.onReject('Unable to retrieve data source');
            }
          });
        }
        resolvers.current.remove(dataKey);
        setQueueIsLocked(false);
        return;
      }

      // account for any API response mutation that needs to happen
      sourceDataMutate(dataSources[variables.prefix], {
        getActionHeaderTitle,
        resolveObjectTypeById,
        resolvePropertyOptionLabel
      }).then(mutatedData => {
        setData(mutatedData);
        setLoading(false);
        if (resolversForRequest) {
          resolversForRequest.forEach(resolver => {
            if (resolver.onResolve) {
              resolver.onResolve(mutatedData);
            }
          });
        }
        resolvers.current.remove(dataKey);
        setQueueIsLocked(false);
      }).catch(() => {
        setLoading(false);
        if (resolversForRequest) {
          resolversForRequest.forEach(resolver => {
            if (resolver.onReject) {
              resolver.onReject('Source data mutated failed');
            }
          });
        }
        resolvers.current.remove(dataKey);
        setQueueIsLocked(false);
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryData, error, variables]);
  const makeQueryVariables = useCallback(prefixReq => {
    const {
      prefix: prefixUnvalidated,
      options
    } = prefixReq;
    const {
      query = {}
    } = options || {};
    const queryPrefix = rewriteLegacyPrefix(prefixUnvalidated);
    return {
      flowId,
      flowObjectTypeId,
      prefix: queryPrefix,
      query
    };
  }, [flowId, flowObjectTypeId]);
  const getDataSource = useCallback(prefixReq => {
    setRequestQueue(previousQueue => previousQueue.add(makeQueryVariables(prefixReq)));
  }, [makeQueryVariables]);
  const getDataSourceWithAPromise = useCallback(prefixReq => {
    return new Promise((onResolve, onReject) => {
      const dataKey = JSON.stringify(makeQueryVariables(prefixReq));
      const resolversForRequest = resolvers.current.get(dataKey) || ImmutableList();
      resolvers.current = resolvers.current.set(dataKey, resolversForRequest.push({
        onResolve,
        onReject
      }));
      getDataSource(prefixReq);
    });
  }, [getDataSource, makeQueryVariables]);
  return {
    getDataSource,
    data,
    loading,
    error,
    getDataSourceWithAPromise
  };
};